import type { BadgeVariant } from "../../common/Badge";
import type { TerminationRequestDto } from "./types";

/** User-facing labels for raw `terminationStatus` codes from the API. */
const TERMINATION_STATUS_LABELS: Record<string, string> = {
  TERMINATION_INITIATED: "Termination initiated",
  HR_REVIEW: "HR review",
  NOTICE_PERIOD: "Termination approved",
  REJECTED: "Rejected",
  TERMINATION_CANCELLED: "Termination cancelled",
  LINKED_TERMINATION_CANCELLED: "Termination cancelled",
  TERMINATION_COMPLETED: "Exit completed",
};

/** Badge variant for termination list / detail status column. */
export function getTerminationRowStatusVariant(
  row: TerminationRequestDto,
): BadgeVariant {
  const w = row.workflow;
  if (w?.isCancelled) return "gray";
  if (w?.isRejected) return "red";
  if (w?.isCompleted) {
    return "green";
  }
  return "yellow";
}

/** Human-readable label for API terminationStatus / workflow combo */
export function terminationListStatusLabel(row: TerminationRequestDto): string {
  const w = row.workflow;
  if (w?.isCancelled) return "Termination cancelled";
  if (w?.isRejected) return "Rejected";
  if (w?.isCompleted) {
    const s = row.terminationStatus?.toUpperCase() ?? "";
    if (s === "NOTICE_PERIOD" || s.includes("NOTICE"))
      return "Termination approved";
    if (s === "TERMINATION_COMPLETED") return "Exit completed";
    return "Termination approved";
  }
  if (typeof w?.workflowStatusLabel === "string" && w.workflowStatusLabel) {
    return w.workflowStatusLabel;
  }
  if (typeof row.terminationStatus === "string" && row.terminationStatus) {
    const key = row.terminationStatus.toUpperCase();
    if (TERMINATION_STATUS_LABELS[key]) return TERMINATION_STATUS_LABELS[key];
    return humanizeTerminationCode(row.terminationStatus);
  }
  return "—";
}

function humanizeTerminationCode(code: string): string {
  return code
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function terminationCanReview(
  row: TerminationRequestDto,
): boolean {
  const w = row.workflow;
  if (w?.isCompleted || w?.isRejected || w?.isCancelled) return false;
  const s = (row.terminationStatus ?? "").toUpperCase();
  if (s.includes("CANCELLED")) return false;
  if (s.includes("REJECTED")) return false;
  return true;
}

/** Display name only in grid and modals (no id suffix). */
export function terminationEmployeeLabel(row: TerminationRequestDto): string {
  const e = row.employee;
  const name =
    typeof e?.fullName === "string" ? e.fullName.trim() : "";
  if (name) return name;
  const id =
    (typeof row.employeeId === "string" && row.employeeId) ||
    (typeof e?.userId === "string" && e.userId) ||
    (typeof e?.employeeCode === "string" && e.employeeCode);
  return id || "—";
}
